import { useQuery } from '@apollo/client/react';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faVideo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ZonedDateTime, ZoneId } from '@js-joda/core';
import React, { useMemo, useState } from 'react';
import {
  CoachSessionOrder,
  CoachSessionStyle,
  CoachSessionsDocument,
  OrderDirection,
} from '../../../graphql/types';
import { useDelayedCallback } from '../../../hooks/useDelayedCallback';
import useTracking from '../../../hooks/useTracking';
import {
  getLocalTimeUtcEndOfDay,
  getLocalTimeUtcStartOfDay,
  time,
} from '../../../utils/date';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
} from '../../../utils/trackingEvents';
import { isSessionJoinable } from '../../Sessions/util';
import { AlertBanner } from '../AlertBanner';
import { ButtonIconPosition } from '../Button';
import { Link } from '../Link';

interface JoinSessionBannerProps {
  testNow?: ZonedDateTime;
}

export const JoinSessionBanner: React.FC<JoinSessionBannerProps> = ({
  testNow,
}) => {
  const { track } = useTracking();
  const [displayBanner, setDisplayBanner] = useState(false);

  const now = useMemo(
    () => testNow || ZonedDateTime.now(ZoneId.UTC),
    [testNow],
  );

  const { data, loading, error, refetch } = useQuery(CoachSessionsDocument, {
    variables: {
      orderBy: [
        {
          order: CoachSessionOrder.SessionTime,
          direction: OrderDirection.Desc,
        },
      ],
      filter: {
        dateRange: {
          /**
           * start and end of day are used to maintain consistent request variables
           * to allow caching similar requests, avoiding re-requesting the same data
           * as we switch pages
           */
          start: getLocalTimeUtcStartOfDay().toString(),
          end: getLocalTimeUtcEndOfDay(
            now.plusDays(1).toLocalDateTime(),
          ).toString(),
        },
        coachSessionStyle: [CoachSessionStyle.Virtual],
      },
    },
    fetchPolicy: 'cache-first',
  });

  const sessions = data?.self?.coachData?.sessions.edges.map(
    ({ node }) => node,
  );

  const joinableSession = sessions?.find(
    (session) => isSessionJoinable(session),
    now,
  );
  const nextSession =
    joinableSession ||
    sessions?.find(
      (session) =>
        ZonedDateTime.parse(session.joinableTime).isAfter(now) &&
        session.meetingUrl,
    );

  useDelayedCallback(
    nextSession && nextSession.joinableTime
      ? ZonedDateTime.parse(nextSession.joinableTime)
      : now,
    () => setDisplayBanner(true),
  );
  useDelayedCallback(
    nextSession && nextSession.calendar?.end
      ? ZonedDateTime.parse(nextSession.calendar.end)
      : now,
    () => setDisplayBanner(false),
  );

  useDelayedCallback(now.plusHours(24), () => {
    refetch();
  });

  if (!displayBanner || loading || error) {
    return null;
  }

  const handleMemberProfileClick = () => {
    track(EventNames.MemberClick, {
      member_id: nextSession?.member?.id,
      session_id: nextSession?.id,
      event_category: EventCategories.Sessions,
      card_name: EventProfileCardNames.JoinSessionBanner,
    });
  };

  const handleJoinSession = () => {
    track(EventNames.ButtonClick, {
      session_id: nextSession?.id,
      event_category: EventCategories.Sessions,
      event_label: EventLabels.JoinSession,
      card_name: EventProfileCardNames.JoinSessionBanner,
    });
    if (nextSession?.meetingUrl) {
      window.open(nextSession.meetingUrl, '_blank');
    }
  };

  const formattedMemberName = `${nextSession?.member.firstName} ${nextSession?.member.lastName?.charAt(0)}.`;
  const formattedSessionTime =
    nextSession?.calendar?.start && time(nextSession.calendar.start);
  const joinText = (
    <span>
      Your virtual Coaching Session with{' '}
      <Link
        to={`/app/members/${nextSession?.member.id}`}
        onClick={handleMemberProfileClick}
      >
        {formattedMemberName}
      </Link>{' '}
      starts at {formattedSessionTime}.
    </span>
  );

  return (
    <AlertBanner
      icon={faClock}
      secondaryText={joinText}
      ctaText="Join Session"
      ctaOnClick={handleJoinSession}
      ctaIcon={<FontAwesomeIcon icon={faVideo} fontSize={16} />}
      ctaIconPosition={ButtonIconPosition.Left}
    />
  );
};
